import { Box, FormControl, TextInput } from "@primer/react";
import moment from "moment";
import { useState } from "react";
import { khoLeadApi } from "../../../api/khoLeadApi";
import SelectBoxSingle from "../../../components/selectbox-single";
import Button from "../../../components/ui/button";
import Modal from "../../../components/ui/modal";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { IKhoLeadInteractionVm } from "../../../models/response/crm-survey/IKhoLead";
import { textareaSx } from "./khoLeadStyles";

interface IKhoLeadInteractionEditModalProps {
    leadId: number;
    interaction: IKhoLeadInteractionVm;
    loaiLienHes: { id: number; ten_loai: string; co_ngay_hen: boolean }[];
    ketQuaLienHes: { ten_ket_qua: string; co_ngay_hen: boolean }[];
    onClose: () => void;
    onSuccess: () => void;
}

// Sửa một tương tác Lead, cùng các trường và quy tắc với form "Thêm lịch sử chăm sóc"
const KhoLeadInteractionEditModal = ({ leadId, interaction, loaiLienHes, ketQuaLienHes, onClose, onSuccess }: IKhoLeadInteractionEditModalProps) => {
    const { translate } = useCommonContext();
    const [loaiId, setLoaiId] = useState<number | undefined>(interaction.dm_loai_lien_he_id);
    const [result, setResult] = useState<string>(interaction.result ?? "");
    const [content, setContent] = useState<string>(interaction.content ?? "");
    const [appointment, setAppointment] = useState<string>(
        interaction.appointment ? moment(interaction.appointment).format("YYYY-MM-DD") : ""
    );
    const [note, setNote] = useState<string>(interaction.note ?? "");
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const needAppointment = !!loaiLienHes.find(x => x.id === loaiId)?.co_ngay_hen
        || !!ketQuaLienHes.find(x => x.ten_ket_qua === result)?.co_ngay_hen;

    const handleSave = async () => {
        if (!content.trim()) {
            NotifyHelper.Warning(translate("Vui lòng nhập nội dung trao đổi"));
            return;
        }
        if (loaiId === undefined) {
            NotifyHelper.Warning(translate("Vui lòng chọn loại liên hệ"));
            return;
        }
        if (needAppointment && !appointment) {
            NotifyHelper.Warning(translate("Vui lòng chọn lịch hẹn"));
            return;
        }
        setIsSaving(true);
        try {
            const res = await khoLeadApi.updateInteraction(leadId, interaction.id, {
                dm_loai_lien_he_id: loaiId,
                result: result || undefined,
                content: content.trim(),
                appointment: needAppointment ? (appointment || undefined) : undefined,
                note: note.trim() || undefined,
            });
            if (res.is_success) {
                NotifyHelper.Success(translate("Đã cập nhật lịch sử chăm sóc"));
                onSuccess();
            } else {
                NotifyHelper.Error(res.message ?? translate("Có lỗi xảy ra"));
            }
        } catch (e: any) {
            NotifyHelper.Error(e?.message ?? translate("Có lỗi xảy ra"));
        } finally {
            setIsSaving(false);
        }
    };

    const renderFooter = () => (
        <Box sx={{
            display: "flex", justifyContent: "flex-end", gap: 2,
            p: 3, borderTop: "1px solid", borderColor: "border.default", flexShrink: 0,
        }}>
            <Button text="Base.Label.Close" onClick={onClose} disabled={isSaving} />
            <Button text="Lưu" variant="primary" isLoading={isSaving} onClick={handleSave} />
        </Box>
    );

    return (
        <Modal isOpen width="large" title={translate("Sửa lịch sử chăm sóc")} onClose={onClose} renderFooter={renderFooter}>
            <Box sx={{ display: "grid", gap: 3 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: ["1fr", "1fr 1fr"], gap: 3 }}>
                    <FormControl>
                        <FormControl.Label>{translate("Loại liên hệ")}</FormControl.Label>
                        <SelectBoxSingle
                            value={loaiId === undefined ? undefined : String(loaiId)}
                            onValueChanged={v => setLoaiId(v === undefined ? undefined : Number(v))}
                            items={loaiLienHes.map(x => ({ id: String(x.id), text: x.ten_loai }))}
                        />
                    </FormControl>
                    {/* Lịch hẹn đứng ngay sau Loại liên hệ khi cấu hình cần ngày hẹn */}
                    {needAppointment && (
                        <FormControl>
                            <FormControl.Label>
                                {translate("Lịch hẹn")}<Box as="span" sx={{ color: "danger.fg" }}> *</Box>
                            </FormControl.Label>
                            <TextInput block type="date"
                                value={appointment}
                                onChange={e => setAppointment(e.target.value)}
                            />
                        </FormControl>
                    )}
                    <FormControl>
                        <FormControl.Label>{translate("Kết quả liên hệ")}</FormControl.Label>
                        <SelectBoxSingle
                            value={result || undefined}
                            placeholder={translate("Chọn kết quả")}
                            onValueChanged={v => setResult(v === undefined ? "" : String(v))}
                            items={ketQuaLienHes.map(r => ({ id: r.ten_ket_qua, text: r.ten_ket_qua }))}
                        />
                    </FormControl>
                </Box>
                <FormControl>
                    <FormControl.Label>
                        {translate("Nội dung")}<Box as="span" sx={{ color: "danger.fg" }}> *</Box>
                    </FormControl.Label>
                    <Box as="textarea" value={content}
                        onChange={(e: any) => setContent(e.target.value)}
                        sx={{ ...textareaSx, minHeight: "96px" }}
                    />
                </FormControl>
                <FormControl>
                    <FormControl.Label>{translate("Ghi chú thêm")}</FormControl.Label>
                    <Box as="textarea" value={note}
                        onChange={(e: any) => setNote(e.target.value)}
                        sx={{ ...textareaSx, minHeight: "64px" }}
                    />
                </FormControl>
            </Box>
        </Modal>
    );
};

export default KhoLeadInteractionEditModal;
